import React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system/theme/reviewDesk';
import { useResponsive } from '@/hooks/useResponsive';

/**
 * Shared sheet / modal primitive (de-slop FE-18).
 *
 * - Phones (<760): a bottom sheet, full width, anchored to the bottom edge.
 * - Tablet and up: a centered card.
 * - Height is capped with `dvh` on web (the *visible* viewport, so the bottom
 *   of the sheet never sits under the iOS browser toolbar the way `vh` does)
 *   and the body scrolls inside the sheet.
 * - Bottom padding includes `env(safe-area-inset-bottom)` on web (needs
 *   `viewport-fit=cover`, see app/+html.tsx).
 */
export interface SheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Accessibility label for the close button (translated by the caller). */
  closeLabel?: string;
  testID?: string;
  /** Wrap the body in a ScrollView (default true). */
  scroll?: boolean;
  /** Max width of the centered card from tablet up (default 560). */
  maxWidth?: number;
  contentStyle?: StyleProp<ViewStyle>;
}

const isWeb = Platform.OS === 'web';

export function Sheet({
  visible,
  onClose,
  title,
  children,
  closeLabel = 'Close',
  testID,
  scroll = true,
  maxWidth = 560,
  contentStyle,
}: SheetProps) {
  const { isPhone } = useResponsive();

  const body = scroll ? (
    <ScrollView
      style={styles.scroller}
      contentContainerStyle={[styles.body, contentStyle]}
      keyboardShouldPersistTaps="handled"
    >
      {children}
    </ScrollView>
  ) : (
    <View style={[styles.body, styles.scroller, contentStyle]}>{children}</View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isPhone ? 'slide' : 'fade'}
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={[styles.overlay, isPhone ? styles.overlayPhone : styles.overlayCentered]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={closeLabel}
          testID={testID ? `${testID}-backdrop` : undefined}
        />
        <View
          testID={testID}
          accessibilityViewIsModal
          style={[
            styles.sheet,
            isPhone ? styles.sheetPhone : [styles.sheetCentered, { maxWidth }],
            isWeb && (isPhone ? styles.webMaxHeightPhone : styles.webMaxHeightCentered),
            isWeb && isPhone && styles.webSafeAreaBottom,
          ]}
        >
          {isPhone && <View style={styles.grabber} />}
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={1} accessibilityRole="header">{title ?? ''}</Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={closeLabel}
              hitSlop={8}
              style={styles.closeButton}
              testID={testID ? `${testID}-close` : undefined}
            >
              <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
            </Pressable>
          </View>
          {body}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(16, 24, 40, 0.45)',
  },
  overlayPhone: {
    justifyContent: 'flex-end',
  },
  overlayCentered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  sheet: {
    backgroundColor: reviewDesk.card,
    overflow: 'hidden',
  },
  sheetPhone: {
    width: '100%',
    maxHeight: '92%',
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
  },
  sheetCentered: {
    width: '100%',
    maxHeight: '85%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: reviewDesk.line,
  },
  webMaxHeightPhone: {
    maxHeight: '92dvh',
  } as any,
  webMaxHeightCentered: {
    maxHeight: '85dvh',
  } as any,
  webSafeAreaBottom: {
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  } as any,
  grabber: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: reviewDesk.line,
    marginTop: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingLeft: 20,
    paddingRight: 8,
    paddingVertical: 6,
    minHeight: 52,
  },
  title: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
  },
  closeButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
  },
  scroller: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  body: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
});

export default Sheet;
